import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { KeyRound, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { debugLog } from '@/lib/videoPlayerDebug';
import { MIN_PASSWORD_LENGTH } from '@/lib/constants';

interface ResetPasswordProps {
  /**
   * Called after the password is successfully changed, so the app can leave
   * recovery mode and resume normal routing.
   */
  onComplete?: () => void;
}

/**
 * Landing page for the emailed password-recovery link.
 *
 * By the time this renders, Supabase has already exchanged the recovery token
 * for a session — so the user is technically signed in. That's exactly why this
 * screen must be unskippable: without it the recovery link silently logs people
 * in and never asks for a new password (the bug in #8).
 */
const ResetPassword: React.FC<ResetPasswordProps> = ({ onComplete }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { toast } = useToast();

  // Supabase puts recovery failures in the URL hash (expired or already-used
  // link). Surface it rather than showing a form that can't work.
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const linkError = hashParams.get('error_description')?.replace(/\+/g, ' ');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    debugLog.userAction("User clicked 'Set New Password'");
    setError('');

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters long`);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;

      toast({
        title: 'Password updated',
        description: 'You are now signed in with your new password.',
      });

      // Clear the recovery tokens out of the URL so a refresh doesn't re-enter
      // this screen with a spent link.
      window.history.replaceState(null, '', window.location.pathname);
      onComplete?.();
    } catch (err) {
      console.error('[ResetPassword] Failed to update password:', err);
      setError(err instanceof Error ? err.message : 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-400 via-pink-500 to-purple-600 flex items-center justify-center px-4">
      <Card className="w-full max-w-md bg-white/10 backdrop-blur-md border-white/20 text-white">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <KeyRound className="w-12 h-12 text-white/90" />
          </div>
          <CardTitle className="text-2xl">Set a New Password</CardTitle>
        </CardHeader>
        <CardContent>
          {linkError ? (
            <div className="space-y-4 text-center">
              <p className="text-red-300">{linkError}</p>
              <p className="text-sm text-white/70">
                Password reset links expire and can only be used once. Request a
                new one from the sign-in page.
              </p>
              <Button
                onClick={() => {
                  debugLog.userAction("User clicked 'Back to Sign In' (expired reset link)");
                  window.location.href = '/';
                }}
                className="w-full bg-white/20 hover:bg-white/30 text-white border border-white/30"
              >
                Back to Sign In
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-white">
                  New password
                </Label>
                <Input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                  autoComplete="new-password"
                  placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                  className="bg-white/20 border-white/30 text-white placeholder:text-white/60"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-white">
                  Confirm password
                </Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  placeholder="Re-enter your new password"
                  className="bg-white/20 border-white/30 text-white placeholder:text-white/60"
                />
              </div>

              {error && <p className="text-red-300 text-sm">{error}</p>}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-white/20 hover:bg-white/30 text-white border border-white/30"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Set New Password'
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
